/**
 * InsForge client singleton for the platform console.
 *
 * Separate auth realm from the client backoffice: this client authenticates
 * platform super-users (platform_users) and never carries a tenant grant.
 * Configured from Vite env vars (see vite-env.d.ts).
 */

import { createClient } from '@insforge/sdk';

// Auth cookies must be first-party for the session refresh to survive in real
// browsers. The console (served on *.insforge.site / platform.obrava.app) and
// the InsForge API (*.insforge.app) are different registrable domains, so the
// refresh cookie would be a third-party cookie that Chrome blocks — breaking
// token refresh and, with it, MFA enrolment.
//
// In production we therefore talk to our OWN origin and let a same-origin
// reverse proxy forward `/api/*` and `/functions/*` to the InsForge backend
// (see apps/platform-console/vercel.json). That keeps every auth cookie
// first-party. Enable it per-environment with VITE_INSFORGE_SAME_ORIGIN=true.
//
// In local dev there is no proxy (Vite doesn't run vercel.json rewrites), so we
// hit the InsForge URL directly — the dev browser still allows the cross-site
// cookie, so the flow works as-is.
//
// Default: every PRODUCTION build self-proxies (same origin); dev hits the API
// directly. Override either way with VITE_INSFORGE_SAME_ORIGIN ('true'/'false').
const flag = import.meta.env.VITE_INSFORGE_SAME_ORIGIN;
const sameOrigin = flag === 'true' || (flag !== 'false' && import.meta.env.PROD);
const baseUrl = sameOrigin ? window.location.origin : import.meta.env.VITE_INSFORGE_URL;

// Edge functions: always go through the gateway path (`<baseUrl>/functions/:slug`)
// — same-origin in production, the InsForge host in dev. Left unset, the SDK first
// tries the legacy Deno Subhosting host `https://{appKey}.functions.insforge.app`,
// dead since the Deno Deploy Classic sunset (2026-07-20); its CORS-less 404 makes
// the browser fetch throw a TypeError instead of an InsForgeError(404), so the
// SDK's own fallback to the gateway never runs and every invoke() fails. In
// production `baseUrl` is our own origin, so the derivation was already skipped;
// this makes local dev behave the same.
const functionsUrl = `${baseUrl.replace(/\/+$/, '')}/functions`;

export const insforge = createClient({
  baseUrl,
  anonKey: import.meta.env.VITE_INSFORGE_ANON_KEY,
  functionsUrl,
});
